import { Component, EventEmitter, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {StmtAttrData, StmtData} from '../../../../../../shared/models/dst-form-state.model';
import {StatementTrackerService} from '../../../../../statement-tracker.service';
import {CreateDisclosureSharedService, DropDownType} from '../../../create-disclosure-shared.service';
import {DSTEntityDetails} from '../../template-leid-selector/template-leid-selector.component';

@Component({
    selector: 'gtw-view-template-form8858-id400',
    templateUrl: './view-template-form8858-id400.component.html',
    styleUrls: ['../../template-shared.scss', './view-template-form8858-id400.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ViewTemplateForm8858Id400Component implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  @Output('onCancel') onCancel: EventEmitter<null> = new EventEmitter<null>();

  form400!: UntypedFormGroup;
  stmtData!: StmtData;
  attMap: any;
  loaded: boolean = false;
  startLoader: boolean = false;
  disableActions: boolean = false;

  // Resolved display values
  countryValue: string = '';
  stateValue: string = '';

  CountryOptions: any[] = [];
  StateOptions: any[] = [];

  // FDE/FB Type resolved text
  fdeFBTypeText: string = '';
  returnStatusText: string = '';

  // Tax Owner display fields (loaded from entity lookup; edit view parity without LEID)
  taxOwnerName: string = '';
  taxOwnerAddressLine1: string = '';
  taxOwnerAddressLine2: string = '';
  taxOwnerCity: string = '';
  taxOwnerStateName: string = '';
  taxOwnerZip: string = '';
  taxOwnerEin: string = '';

  // Schedule G flags
  SeparateUnitDefinedInRegInd: boolean | undefined;
  DualCnsldtLossTaxableIncomeInd: boolean | undefined;
  PermittedDomDualCnsldtLossInd: boolean | undefined;
  PrmtDomDualCnsldtLossDcmntnInd: boolean | undefined;
  CmptDualCnsldtLossTxblIncmInd: boolean | undefined;
  TriggeringEventRecaptureInd: boolean | undefined;

  constructor(private formBuilder: UntypedFormBuilder,
              private sharedService: CreateDisclosureSharedService,
              private statementTrackerService: StatementTrackerService) {
    this.disableActions = this.sharedService.disableActions;
    this.sharedService.disableActions = false;
  }

  ngOnInit(): void {
    this.startLoader = true;
    this.form400 = this.formBuilder.group({
      header: this.formBuilder.group({
        consolGroup: ['', Validators.required],
        options: ['']
      })
    });

    this.sharedService.getCountryDropdown().pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: DropDownType[]) => {
        data.forEach(element => {
          this.CountryOptions.push({
            value: element.id,
            label: element.text
          });
        });
      }, () => {
      }, () => {
      });

    this.sharedService.getStateDropdown().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: DropDownType[]) => {
      data.forEach(element => {
        this.StateOptions.push({
          value: element.id,
          label: element.text
        });
      });
    }, () => {
    }, () => {
    });

    // Load statement data
    this.sharedService.getLoadStatementData(400).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: StmtData) => {
        this.stmtData = response;
        this.attMap = {};
        this.stmtData.children?.forEach((stmt: StmtAttrData) => {
          if (!this.attMap[stmt.attributeName]) {
            this.attMap[stmt.attributeName] = [];
          }
          if (stmt.attributeValue !== undefined) {
            this.attMap[stmt.attributeName]?.push(stmt.attributeValue);
          } else {
            this.attMap[stmt.attributeName]?.push('');
          }
        });

        // FDE/FB Type
        if (this.attMap['f8858FbusPersonInd'] && this.attMap['f8858FbusPersonInd'][0]) {
          const val = this.attMap['f8858FbusPersonInd'][0];
          this.fdeFBTypeText = val === 'FDE' ? 'FDE of a U.S. Person' : (val === 'FB' ? 'FB of a U.S. Person' : val);
        }

        // Return Status
        if (this.attMap['f8858InitialFormInd'] && this.attMap['f8858InitialFormInd'][0]) {
          const val = this.attMap['f8858InitialFormInd'][0];
          this.returnStatusText = val === 'Initial' ? 'Initial Form 8858' : (val === 'Final' ? 'Final Form 8858' : val);
        }

        // Restore Schedule G booleans
        if (this.attMap['f8858SchGSepUnitDefRegInd'] && this.attMap['f8858SchGSepUnitDefRegInd'][0]) {
          this.SeparateUnitDefinedInRegInd = this.attMap['f8858SchGSepUnitDefRegInd'][0] === '1';
        }
        if (this.attMap['f8858SchGDualCnslLossTxIncInd'] && this.attMap['f8858SchGDualCnslLossTxIncInd'][0]) {
          this.DualCnsldtLossTaxableIncomeInd = this.attMap['f8858SchGDualCnslLossTxIncInd'][0] === '1';
        }
        if (this.attMap['f8858SchGPermCnslLossInd'] && this.attMap['f8858SchGPermCnslLossInd'][0]) {
          this.PermittedDomDualCnsldtLossInd = this.attMap['f8858SchGPermCnslLossInd'][0] === '1';
        }
        if (this.attMap['f8858SchGDualCnslLossDcmntnInd'] && this.attMap['f8858SchGDualCnslLossDcmntnInd'][0]) {
          this.PrmtDomDualCnsldtLossDcmntnInd = this.attMap['f8858SchGDualCnslLossDcmntnInd'][0] === '1';
        }
        if (this.attMap['f8858SchGCmpDualCnslLossTxInd'] && this.attMap['f8858SchGCmpDualCnslLossTxInd'][0]) {
          this.CmptDualCnsldtLossTxblIncmInd = this.attMap['f8858SchGCmpDualCnslLossTxInd'][0] === '1';
        }
        if (this.attMap['f8858SchGTrigRecapInd'] && this.attMap['f8858SchGTrigRecapInd'][0]) {
          this.TriggeringEventRecaptureInd = this.attMap['f8858SchGTrigRecapInd'][0] === '1';
        }

        this.loadTaxOwnerDetails();

      }, () => {
      }, () => {
      }).add(() => {
      this.loaded = true;
      this.startLoader = false;
    });
  }

  save(): void {
    this.sharedService.setFormData(this.stmtData);
    this.sharedService.setSavedStatementId(this.stmtData.statementid.toString());
    this.sharedService.switchTemplateMode(false);
  }

  cancel(): void {
    this.onCancel.emit();
  }

  // 10a = Yes drives visibility of 11b, 11c
  get show10aYesSection(): boolean {
    return this.SeparateUnitDefinedInRegInd === true;
  }

  // 10a = No drives visibility of 10b
  get show10aNoSection(): boolean {
    return this.SeparateUnitDefinedInRegInd === false;
  }

  // 12a !== false drives visibility of 12b
  get show12b(): boolean {
    return this.DualCnsldtLossTaxableIncomeInd !== false;
  }

  // 12c visible only when 12a is not No AND 12b is Yes
  get show12c(): boolean {
    return this.show12b && this.PermittedDomDualCnsldtLossInd === true;
  }

  // 12d visible when 12a is not No AND (12b is No OR (12b is Yes AND 12c is not Yes))
  get show12d(): boolean {
    if (!this.show12b || this.PermittedDomDualCnsldtLossInd == null) {
      return false;
    }
    if (this.PermittedDomDualCnsldtLossInd === false) {
      return true;
    }
    return this.PrmtDomDualCnsldtLossDcmntnInd !== true;
  }

  private loadTaxOwnerDetails(): void {
    const branchLeid = this.stmtData?.branchleid;
    if (!branchLeid) {
      return;
    }

    this.sharedService.getEntityList(branchLeid, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((entities) => {
        if (!entities?.length) {
          return;
        }

        const taxOwner = entities[0];
        this.taxOwnerName = taxOwner.ENTITY || this.stmtData.branchentity || '';
        this.taxOwnerAddressLine1 = taxOwner.ADDRESS1 || '';
        this.taxOwnerAddressLine2 = taxOwner.ADDRESS2 || '';
        this.taxOwnerCity = taxOwner.CITY || '';
        this.taxOwnerZip = taxOwner.ZIP || '';
        this.taxOwnerEin = taxOwner.EIN || '';

        const selectedState = this.StateOptions.find((state: any) => state.value === taxOwner.STATE);
        this.taxOwnerStateName = selectedState?.label || taxOwner.STATEINC || taxOwner.STATE || '';
      });
  }

}
